// @givmo/api-client/list — shared tolerant list-decode helpers.
//
// Every Givmo list endpoint shares two facts that force a tolerant read at the
// call site, regardless of which frontend consumes it:
//
//   1. ENVELOPE-UNWRAP RUNTIME/TYPE MISMATCH. The backend wraps paginated list
//      payloads as `{ data, meta, links }` (`generate_paginated_dict_response`).
//      The @givmo/api-client response middleware (`middleware/index.ts`) REWRITES
//      the body to the bare `data` array when it detects that envelope
//      (shape-gated on `meta`/`links`). So although a generated TanStack-Query
//      option may type the resolved data as an `XxxPaginatedResponse` envelope,
//      at RUNTIME the query receives the bare row array. `extractList` reads BOTH
//      shapes (bare array, or an object carrying a `data` array) so the screen is
//      correct whether or not the middleware unwrapped — and never throws.
//
//      Some endpoints instead return a NAMED wrapper the envelope middleware does
//      NOT unwrap (it is shape-gated on the paginated `{data} + meta|links`
//      envelope) — e.g. `CharityAdminListOut` `{ admins:[…] }`,
//      `CharityClaimRequestListOut` `{ requests:[…] }`,
//      `Section321GrantListOut` `{ grants:[…] }`. Pass the wrapper's key as
//      `namedKey` and `extractList` will read it, still falling back to the bare
//      array / `{data}` envelope.
//
//   2. NON-THROWING DATE FORMATTING. `toDateOnly` renders an ISO `YYYY-MM-DD` and
//      tolerates null / undefined / unparseable values (returns ''), so a
//      malformed timestamp never crashes a row.
//
// These are screen-agnostic; feature-specific decode (e.g. moderation's
// `isModerated`, derived from a `*_at` audit timestamp) layers on top in the
// feature's own decode module.

/** Narrow an unknown value to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Tolerantly extract a list of rows from a list-endpoint response. Handles:
 *   - the bare `T[]` array (the common case — the response middleware unwrapped
 *     the `{data,meta,links}` envelope);
 *   - a NAMED wrapper `{ [namedKey]: T[] }` (e.g. `{ admins:[…] }`), when a
 *     `namedKey` is supplied — checked BEFORE `data`;
 *   - the un-unwrapped `{ data: T[] }` envelope;
 * and returns `[]` for anything else (null/undefined/object without a matching
 * array). Never throws.
 */
export function extractList<T>(body: unknown, namedKey?: string): T[] {
  if (Array.isArray(body)) return body as T[];
  if (isRecord(body)) {
    if (namedKey !== undefined) {
      const named = body[namedKey];
      if (Array.isArray(named)) return named as T[];
    }
    const data = body.data;
    if (Array.isArray(data)) return data as T[];
  }
  return [];
}

/**
 * A non-throwing date-only formatter (YYYY-MM-DD) for created/redeemed/listed
 * columns. Tolerates null / undefined / unparseable values (returns '').
 */
export function toDateOnly(value: string | null | undefined): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Locale-independent ISO date (matches the Flutter `toDateOnly()` YYYY-MM-DD).
  return date.toISOString().slice(0, 10);
}
